import { CurrencyType } from "..";

export interface QuickViewQueries {
  filters: Record<string, Array<string | number | boolean>>;
  columns: {
    calculatedFields: string[];
    characteristics: string[];
    measures: string[];
    visibleSystemColumns: string[]; // System columns: "investor", "round", "last_update"
    columnsOrder: string[]; // Ordered list of all column keys across all groups
  };
  groupByDeal: boolean;
}

export interface IQuicViewTableRowObj {
  id?: number;
  value?: string;
  title?: string;
  name?: string;
  updated_at?: string;
  updated_by?: string | null;
  editable: boolean;
  date?: string;
  postfix?: string;
  measure_id?: number;
  measure_data_id?: number;
  characteristic_id?: number;
  characteristic_data_id?: number;
  asset_class_id?: number;
  sub_asset_class_id?: number;
  uniqueKey: string;
  type?: "number" | "text" | "percentage" | "currency" | "asset" | "date";
  currency?: CurrencyType;
  is_global_value?: boolean;
  is_total: boolean;
  is_currency?: boolean;
  show?: boolean;
  status?: string;
  children: QuickViewTableRow[];
}

export type QuickViewTableRow = {
  [key in QuickViewTableColumn["key"]]: IQuicViewTableRowObj;
} & {
  children: QuickViewTableRow[];
};

export type QuickViewTableColumn = {
  key: string;
  title: string;
  id?: number;
  type?: string;
  tooltip?: string;
};

export type QuickViewTableData = {
  columns: QuickViewTableColumn[];
  rows: QuickViewTableRow[];
  total: number;
};

export const SHEET_DATE_TYPES = {
  ACCOUNTING: "accounting",
  EFFECTIVE: "effective"
} as const;

export type SheetDateType = (typeof SHEET_DATE_TYPES)[keyof typeof SHEET_DATE_TYPES];

export interface SheetSettings {
  dateRange?: { to: string };
  use_accounting_date?: boolean;
  currency?: CurrencyType;
  exchangeDate?: "toDate";
}

export type QuickViewMeasureFilter = Record<string, { from_value?: number; to_value?: number }>;

export interface IGetQuickViewData extends SheetSettings {
  group_by_deal?: boolean;
  search?: string;
  deal_ids?: [];
  characteristics?: string[];
  measure_ids?: string[];
  calculated_fields?: string[];
  filters: Record<string, Array<string | number | boolean>>;
  measure_filters?: QuickViewMeasureFilter;
  page?: number;
  per_page?: number | "infinite";
  order_by_updated_at?: boolean;
}

export interface IGetQuickViewSettingsFilters extends IGetQuickViewData {
  visible_system_columns?: string[];
}

export interface IGetQuickViewSettings {
  data: {
    filters: IGetQuickViewSettingsFilters;
    measures: string[];
  };
  key: any;
  type: "dealQuickView";
}

export interface IGetLatestValuesQuery {
  deal_id: number;
  measure_id: number;
  round_id?: number;
  investor_id?: number;
}

export interface QuickViewMeasureData {
  validation_id: number;
  measure_id?: number;
  measure_data_id: number;
  value: number;
  date: string;
  currency: CurrencyType;
  postfix: string;
  deal_id?: number;
  is_total?: boolean;
  investor_id?: number;
  round_id?: number;
  edited?: boolean;
  description?: string;
}
export interface ILatestValuesResponse {
  validation_id: number;
  measure_id?: number;
  measure_data_id: number;
  value: string;
  date: string;
  currency: CurrencyType;
  postfix: string;
  deal_id?: number;
  is_total?: boolean;
  investor_id?: number;
  round_id?: number;
}

export interface IPopupData {
  elem: HTMLElement;
  title: string;
  dealTitle: string;
  dealId: number;
  dataId?: number; //can be as measureId or characteristicId
  value?: string;
  date?: string;
  type?: "measure" | "characteristic";
  postfix?: string;
  currency?: CurrencyType;
  investor?: { id: number; name: string }; //property to setFilters in monitoringData
  round?: { id: number; name: string }; //property to setFilters in monitoringData
  characteristic_type?: "number" | "text" | "percentage" | "currency" | "asset"; //type of characteristic to define input type
  isGlobal?: boolean;
  is_total?: boolean;
  is_currency?: boolean;
  measureId?: number;
  isAbsolute?: boolean;
  isNegative?: boolean;
  assetId?: number;
  subAssetId?: number | null;
  rowKey?: string;
}

export interface IQuickViewCreateNewItem {
  deal: { id?: number; title?: string; asset_class_id?: number; currency?: string | null };
  round: { id?: number; name?: string };
  investor: { id?: number; name?: string; email?: string; phone_number?: string; company?: string };
  isAbsolute?: boolean;
  isNegative?: boolean;
}
